import type Keycloak from 'keycloak-js'
import type {
    KeycloakError,
    KeycloakInitOptions,
    KeycloakLoginOptions,
    KeycloakLogoutOptions,
} from 'keycloak-js'

import { Session } from '../../machine/types'
import { AdapterInitResult, IdPAdapter } from '../IdPAdapter'
import { ClaimMapperOptions, claimsFromKeycloakToken } from './parseClaims'

export interface KeycloakWebAdapterOptions extends ClaimMapperOptions {
    /** A constructed (but un-initialised) Keycloak instance. */
    keycloak: Keycloak
    /** Init options passed to `keycloak.init()`. */
    initOptions?: KeycloakInitOptions
    /** Options for explicit `login()` calls — typically a `redirectUri`. */
    loginOptions?: KeycloakLoginOptions
    /** Options for explicit `logout()` calls. */
    logoutOptions?: KeycloakLogoutOptions
}

const DEFAULT_INIT: KeycloakInitOptions = {
    checkLoginIframe: false,
    pkceMethod: 'S256',
}

const ERROR_CODES_TERMINAL = new Set([
    'invalid_grant',
    'invalid_token',
    'invalid_client',
    'unauthorized_client',
    'unauthorized',
    'token_expired',
    'token_refresh_failed',
])

const buildSession = (keycloak: Keycloak, options: KeycloakWebAdapterOptions): Session => {
    if (!keycloak.token || !keycloak.tokenParsed) {
        throw new Error('Keycloak instance has no token to build a session from')
    }
    return {
        accessToken: keycloak.token,
        refreshToken: keycloak.refreshToken,
        idToken: keycloak.idToken,
        claims: claimsFromKeycloakToken(keycloak.tokenParsed, options),
    }
}

/**
 * `IdPAdapter` wrapping `keycloak-js`.
 *
 * Notes on Keycloak's redirect flow:
 * - `init({ onLoad: 'login-required' })` will redirect unauthenticated
 *   users to the Keycloak login page during `adapter.init()`. The
 *   Promise then resolves AFTER the redirect-back with a live session.
 * - For an explicit `login()`, Keycloak triggers a full-page redirect.
 *   The returned Promise never resolves in the current page lifetime —
 *   the runtime is happy to leave the FSM in `authenticating` because
 *   the page will reload anyway.
 *
 * Token refresh is handled via `updateToken(-1)` (forced). The internal
 * refresh token is opaque to us; the `refreshToken` parameter passed
 * by the runtime is therefore ignored on web.
 */
export const createKeycloakWebAdapter = (options: KeycloakWebAdapterOptions): IdPAdapter => {
    const { keycloak } = options
    const initOptions: KeycloakInitOptions = { ...DEFAULT_INIT, ...options.initOptions }

    // keycloak-js throws if init() runs twice on the same instance. Memoise the
    // call so an early bootstrap-time init() (used to obtain a token and load
    // app-config before the app chunk imports) and the later AuthProvider
    // init() share a single underlying keycloak.init().
    let initPromise: Promise<AdapterInitResult> | null = null
    const init = (): Promise<AdapterInitResult> => {
        if (initPromise) {
            return initPromise
        }
        initPromise = (async () => {
            try {
                const authenticated = await keycloak.init(initOptions)
                if (authenticated && keycloak.token) {
                    return { session: buildSession(keycloak, options), supported: true }
                }
                return { session: null, supported: true }
            } catch (err) {
                initPromise = null
                throw err instanceof Error ? err : new Error(String(err))
            }
        })()
        return initPromise
    }

    const login = async (): Promise<Session> => {
        // keycloak.login() returns a Promise that never resolves before the
        // full-page redirect. We return the (never-resolving) promise; the
        // runtime stays in `authenticating` until the page reloads.
        return keycloak.login(options.loginOptions) as unknown as Promise<Session>
    }

    const refresh = async (_refreshToken: string | undefined): Promise<Session> => {
        // -1 forces a real refresh regardless of the current token's remaining
        // validity. With a minValidity threshold this was a silent no-op for
        // still-valid tokens, which broke both runtime callers: the first-login
        // provisioning dance never picked up the freshly-minted `aosId` claim
        // (AOS-782 — user.id stuck at the unprovisioned sentinel, disabling every
        // id-keyed query), and a 401-rejected-but-unexpired token was "refreshed"
        // into itself. Mobile's refresh grant always refreshes; this aligns web.
        await keycloak.updateToken(-1)
        return buildSession(keycloak, options)
    }

    const logout = async (_idToken: string | undefined): Promise<void> => {
        await keycloak.logout(options.logoutOptions)
    }

    const isAuthError = (err: unknown): boolean => {
        if (!err) {
            return false
        }
        // keycloak-js rejects with shape `{ error?, error_description? }`
        // and sometimes with a plain Error. Prefer the structured fields
        // (`error`, `code`) — only fall back to substring scanning of the
        // free-form `error_description` / `message` when neither structured
        // field is present. Otherwise a 403 with a description like
        // "client is unauthorized to perform this action" would falsely
        // classify as a terminal auth error and log the user out.
        const e = err as KeycloakError & { code?: string; message?: string }
        const lower = (s?: string) => (typeof s === 'string' ? s.toLowerCase() : undefined)
        const structured = [lower(e?.error), lower(e?.code)].filter(
            (s): s is string => typeof s === 'string',
        )
        if (structured.some(s => ERROR_CODES_TERMINAL.has(s))) {
            return true
        }
        if (structured.length > 0) {
            return false
        }
        const freeForm = [lower(e?.error_description), lower(e?.message)].filter(
            (s): s is string => typeof s === 'string',
        )
        return freeForm.some(s => Array.from(ERROR_CODES_TERMINAL).some(code => s.includes(code)))
    }

    const parseClaims = (accessToken: string) => {
        // We trust the live keycloak instance's parsed token whenever we have one;
        // otherwise we'd need a generic JWT decoder. The runtime calls this only
        // when it already holds a session, so this path is rarely hit.
        if (keycloak.tokenParsed && keycloak.token === accessToken) {
            return claimsFromKeycloakToken(keycloak.tokenParsed, options)
        }
        throw new Error('parseClaims called without a matching live token')
    }

    return {
        name: 'keycloak-web',
        init,
        login,
        refresh,
        logout,
        isAuthError,
        parseClaims,
    }
}
